/**
 * Learning-path AUTHORING — thinlms's own API, for tenant administrators.
 *
 * A second module rather than more functions in `paths.ts`, because it is a different API
 * with a different audience:
 *
 *   paths.ts             /api/learning_paths/v1/   OpenCraft's plugin. Read-only, every
 *                                                  signed-in learner, learner pages only.
 *   this file            /api/pathways/v1/         thinlms's authoring layer. Write, and
 *                                                  only for someone holding an org role.
 *
 * The plugin ships NO write API — both of its pathway viewsets are read-only and its
 * authoring is Django-admin — which is the entire reason this second mount exists.
 *
 * WHAT THE SERVER DECIDES, AND THIS FILE DOES NOT:
 *
 *   who may write        `CanAuthorPathways` wants an org-wide role (instructor, staff or
 *                        content creator) for an org the addressed Host serves. The list
 *                        response hands back `writable_orgs` so a screen can render only
 *                        the controls that will work — see `canWrite` below. That is a
 *                        rendering hint taken FROM the server, not a permission check
 *                        invented here.
 *   which courses fit    adding a course the tenant cannot see is a 403 with a sentence
 *                        naming the course. The UI does not pre-empt that with a rule of
 *                        its own; it offers the tenant's own course list and reports the
 *                        refusal verbatim if the server disagrees.
 *   which paths exist    a path belonging to another tenant answers 404, deliberately
 *                        indistinguishable from one that does not exist.
 */

import { openedx, ApiError } from './openedx';

const AUTHORING_API = '/api/pathways/v1';

/** Path and course keys carry ':' and '+'; both need encoding in a URL segment. */
const seg = (value: string) => encodeURIComponent(value);

/* ==================================================================== types ===== */

export interface AdminPathStep {
  course_key: string;
  order: number;
  weight: number;
}

export interface AdminPath {
  /** `path-v1:{org}+{number}+{run}+{group}`. The org in it is what assigns the tenant. */
  key: string;
  uuid: string;
  display_name: string;
  subtitle: string;
  description: string;
  /** '', 'beginner', 'intermediate' or 'advanced'. */
  level: string;
  duration: string;
  time_commitment: string;
  sequential: boolean;
  /**
   * Upstream's own field, and the raw side of `published`.
   *
   * There is no `published` column: publishing inverts `invite_only`, which defaults to
   * true — so a new path starts unpublished. Both are returned; screens read `published`
   * and this is here so nothing has to guess which way round the inversion goes.
   */
  invite_only: boolean;
  published: boolean;
  org: string;
  created: string;
  modified: string;
  steps: AdminPathStep[];
}

export interface AdminPathList {
  count: number;
  /**
   * The orgs this caller may actually author for on this Host.
   *
   * Returned by the server so an admin screen can render the right controls without a
   * second round trip — and, more importantly, without deciding for itself. A Host may
   * legitimately serve another academy's org in order to show its courses, so "this path
   * is on my list" and "I may edit this path" are genuinely different questions.
   */
  writable_orgs: string[];
  results: AdminPath[];
}

/**
 * The fields a create or an edit may set. Everything else is the server's.
 *
 * This list is `WRITABLE_FIELDS` in `openedx_thinlms_pathways/authoring.py` and nothing
 * else: the wrapper filters an incoming body against that frozenset, so a field invented
 * here would be dropped in silence and the form would appear to save something it did not.
 * Checked against the wrapper at the time of writing — display_name, subtitle, description,
 * level, duration, time_commitment, sequential.
 *
 * NOT here, deliberately: `key` and `uuid` are identity and `editable=False`; `invite_only`
 * is reached only through publish/unpublish; and `image` is not writable over this API at
 * all — the wrapper excludes it from `full_clean` and offers no upload endpoint, so a form
 * field for it would be a control with nowhere to send its value.
 */
export interface PathDraft {
  display_name: string;
  subtitle?: string;
  description?: string;
  /** '', 'beginner', 'intermediate' or 'advanced' — see `LEVELS`. */
  level?: string;
  duration?: string;
  time_commitment?: string;
  sequential?: boolean;
}

/**
 * The only values `level` accepts, and where they come from.
 *
 * Upstream declares `level = CharField(blank=True, choices=LEVEL_CHOICES)` and the wrapper
 * calls `full_clean()` before saving, so anything outside this set comes back as a 400 —
 * which is why the form offers a select and not a text box. `blank=True` is what makes the
 * empty option legitimate rather than a hole: '' is a stored, valid "not stated".
 */
export const LEVELS: { value: string; label: string }[] = [
  { value: '', label: 'Not stated' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

/** The label an admin picked, for a level value that came back from the server. */
export const levelLabel = (value: string): string =>
  LEVELS.find((entry) => entry.value === value)?.label ?? value;

/* ================================================================== reads ===== */

export const listAdminPaths = () => openedx.get<AdminPathList>(`${AUTHORING_API}/paths`);

export const getAdminPath = (key: string) =>
  openedx.get<AdminPath>(`${AUTHORING_API}/paths/${seg(key)}`);

/* ================================================================= writes ===== */

/**
 * Create a path, optionally with its courses already in it.
 *
 * `steps` is a list of course keys, and sending it here is not a convenience wrapper around
 * N calls to `addStep` — the server treats it as part of the create:
 *
 *     views.py PathwayListCreateView.post
 *       assert_may_write_org(...)                 authority, before anything is made
 *       for course_id in steps: assert_course_visible_to_tenant(...)
 *       authoring.create_path(...)                only now does a row exist
 *       for course_id in steps: authoring.add_step(...)
 *
 * so every course is checked against the tenant BEFORE the path is created. A create that
 * names a course this academy cannot see is a 403 that leaves nothing behind, rather than an
 * empty pathway plus an error — which is exactly what building it as create-then-add-each
 * would produce, and the reason a path could previously only be born empty.
 *
 * Order follows list position.
 */
export const createPath = (draft: PathDraft & { key: string; steps?: string[] }) =>
  openedx.post<AdminPath>(`${AUTHORING_API}/paths`, draft);

export const updatePath = (key: string, draft: Partial<PathDraft>) =>
  openedx.patch<AdminPath>(`${AUTHORING_API}/paths/${seg(key)}`, draft);

export const deletePath = (key: string) =>
  openedx.delete<void>(`${AUTHORING_API}/paths/${seg(key)}`);

export const addStep = (key: string, courseKey: string) =>
  openedx.post<AdminPathStep>(`${AUTHORING_API}/paths/${seg(key)}/steps`, {
    course_key: courseKey,
  });

export const removeStep = (key: string, courseKey: string) =>
  openedx.delete<void>(`${AUTHORING_API}/paths/${seg(key)}/steps/${seg(courseKey)}`);

/**
 * Set the WHOLE order, not "move this one".
 *
 * The endpoint takes the complete list and rejects a partial one with a 400 rather than
 * reinterpreting it, which is what makes it idempotent and what makes two steps sharing an
 * `order` impossible. Callers therefore send every current step, every time.
 */
export const setStepOrder = (key: string, order: string[]) =>
  openedx.put<AdminPathStep[]>(`${AUTHORING_API}/paths/${seg(key)}/steps`, { order });

/**
 * Publishing is its own endpoint rather than a field on the update.
 *
 * It is what makes a path visible to every learner on the tenant, and that should not be
 * reachable by mistyping a field name in a form.
 */
export const publishPath = (key: string) =>
  openedx.post<AdminPath>(`${AUTHORING_API}/paths/${seg(key)}/publish`, {});

export const unpublishPath = (key: string) =>
  openedx.post<AdminPath>(`${AUTHORING_API}/paths/${seg(key)}/unpublish`, {});

/* ============================================================== learners ===== */

/**
 * ENROLMENT, for both pathways and courses, over the SAME tenant-scoped mount.
 *
 * Both live at `/api/pathways/v1/` because both are served by one Django app applying one
 * tenant gate — `writable_orgs`, the intersection of the Host's orgs with the orgs the
 * caller holds a role in. The `pathways` in the course URLs is historical naming, not a
 * mistake; see `course_views.py` for why a second app was not worth a nicer word.
 *
 * WHY NOT THE PLATFORM'S OWN ENROLMENT ENDPOINTS. They exist and they work, and they were
 * measured rather than assumed. They are not usable here because neither is tenant-aware:
 *
 *   /api/enrollment/v1/enrollments              `IsAdminUser` — i.e. `is_staff` — and its
 *                                               `course_ids` is a caller-chosen FILTER, not
 *                                               a boundary. Asked unfiltered as MEREKA's
 *                                               admin it returned another tenant's rows too.
 *   /api/instructor/v2/…/enrollments/modify     `HasAccessRule('staff')`, which any
 *                                               GlobalStaff user satisfies for EVERY course
 *                                               on the deployment. MEREKA's admin got a 200
 *                                               writing to another tenant's course.
 *
 * Every thinlms tenant admin is `is_staff=True`, so both doors stood open onto other
 * academies. The wrapper keeps the platform's own enrolment mechanics — including
 * invite-before-registration and the audit trail — and puts our tenant rule in front.
 */

/**
 * One person attached to a pathway or a course.
 *
 * `username` is NULL while an invitation is outstanding, because there is no account yet.
 * That is the whole shape of invite-before-registration and the reason this is one list
 * rather than two: an admin asking "is Sara on this?" should not have to know whether Sara
 * has registered in order to look in the right place.
 */
export interface Member {
  /**
   * `enrolled`  — on it now.
   * `invited`   — an invitation is live, waiting for that email to register.
   * `removed`   — was enrolled, since withdrawn. History.
   * `invite_withdrawn` — an invitation cancelled before it was ever used. PATHWAYS ONLY:
   *                the course side deletes the invitation row outright, so a cancelled
   *                course invitation leaves nothing to show.
   */
  status: 'enrolled' | 'invited' | 'removed' | 'invite_withdrawn';
  username: string | null;
  email: string;
  is_active: boolean;
  /** ISO timestamp, or null where the platform never recorded one. */
  since: string | null;
  /** COURSES ONLY: the enrolment track, or how an invitation will be applied. */
  mode?: string;
  /** COURSES ONLY, and often empty — the platform does not require a full name. */
  name?: string;
}

export interface MemberList {
  /** The wrapper's version, echoed so "is the new code live?" is answerable from a response. */
  api_version: string;
  count: number;
  enrolled: number;
  invited: number;
  results: Member[];
}

/** Live rows first, then history — the order the server already sorted them into. */
export const liveMembers = (members: Member[]): Member[] =>
  members.filter((member) => member.is_active);

/**
 * What an "add one person" write hands back, normalised across the two surfaces.
 *
 * BOTH add endpoints already recompute and return the FULL roster after the write — the
 * server has just rebuilt it to answer the request — and the panel was throwing it away and
 * issuing a GET that rebuilt the identical list a second time. Two full builds for one write.
 *
 * THEY DO NOT AGREE ON THE KEY, which is why this is normalised HERE, in the module that
 * knows which surface it is talking to, and not with a `results ?? results_after` at the
 * call site. That fallback would be a live bug:
 *
 *   pathways  `POST /paths/{key}/learners`     the member list arrives as `results`
 *             (`views.py`: `{"email": ..., **result, "results": list_members(path)}` — the
 *             spread's own `results` is deliberately overwritten by it)
 *   courses   `POST /courses/{id}/learners`    the member list arrives as `results_after`,
 *             and `results` on that response is something else entirely: upstream's per-
 *             address outcome rows, `{before, after}` per person (`course_views.py`). Feeding
 *             those to the roster would replace the list with rows that are not members.
 *
 * `roster` is null when the response carried no usable list — an older image, or a shape this
 * guard does not recognise — and the caller falls back to invalidating. A stale roster is the
 * one outcome worse than a second GET.
 */
export interface RosterWrite {
  email: string;
  /** The roster as the server rebuilt it, or null when it did not send one. */
  roster: Member[] | null;
}

const isMemberRow = (row: unknown): row is Member =>
  typeof row === 'object' &&
  row !== null &&
  typeof (row as Member).email === 'string' &&
  typeof (row as Member).status === 'string' &&
  typeof (row as Member).is_active === 'boolean';

/**
 * A member list, or null.
 *
 * Empty is refused as well as malformed: this only ever reads the answer to an ADD, and a
 * roster with nobody on it immediately after somebody was added is not a result worth
 * trusting — it is a shape we have misread.
 */
const membersOrNull = (value: unknown): Member[] | null =>
  Array.isArray(value) && value.length > 0 && value.every(isMemberRow) ? value : null;

/**
 * Rebuild the list-endpoint's wrapper around a roster the write returned.
 *
 * `count`, `enrolled` and `invited` are recomputed the same way the server computes them —
 * `len(members)` and a count per status (`views.py`, `course_views.py`) — because the write
 * response carries the rows and not the wrapper.
 *
 * SPREAD, NOT REBUILT FIELD BY FIELD, and that is the point of writing it this way. The two
 * list endpoints do not return the same wrapper: the course one adds `course_id`, which this
 * interface does not declare, so a hand-built object drops it and the compiler cannot say so.
 * Nothing reads it today; the bug it would become is a field that is present after a GET and
 * absent after an add, which is about the hardest shape of bug to reproduce.
 *
 * EVERYTHING NOT RECOMPUTED IS CARRIED OVER UNVERIFIED, `api_version` included, and it is
 * worth being plain about that rather than claiming it is true. Neither write response
 * carries `api_version` at all, so there is no fresher value to prefer — and `previous` came
 * from a GET that may predate a rolling deploy. That field exists so "is the new code
 * actually live in this image?" can be answered from a response (the stale-wheel trap), and
 * after a patched write it answers for the GET, not for the write. Ask the list endpoint
 * directly if that is the question.
 */
export const rosterAfterWrite = (previous: MemberList, members: Member[]): MemberList => ({
  ...previous,
  count: members.length,
  enrolled: members.filter((member) => member.status === 'enrolled').length,
  invited: members.filter((member) => member.status === 'invited').length,
  results: members,
});

/* ------------------------------------------------------- pathway learners ---- */

export const listPathLearners = (key: string) =>
  openedx.get<MemberList>(`${AUTHORING_API}/paths/${seg(key)}/learners`);

/** Invite or enrol ONE person, by username or by email. Send one, never both. */
export const addPathLearner = async (
  key: string,
  who: { username?: string; email?: string },
): Promise<RosterWrite> => {
  const body = await openedx.post<{ email: string; results?: unknown }>(
    `${AUTHORING_API}/paths/${seg(key)}/learners`,
    who,
  );
  return { email: body.email, roster: membersOrNull(body.results) };
};

/**
 * A list of emails, all or nothing.
 *
 * The server refuses the WHOLE batch if any address is malformed, rather than inheriting
 * upstream's behaviour of logging a warning and carrying on — fifty pasted rows with one
 * typo would otherwise report success while one person was silently left out.
 */
export const bulkAddPathLearners = (key: string, emails: string[], reason?: string) =>
  openedx.post<{ emails: string[] }>(`${AUTHORING_API}/paths/${seg(key)}/learners/bulk`, {
    emails,
    reason,
  });

export const removePathLearner = (key: string, identifier: string) =>
  openedx.delete<{ email: string }>(
    `${AUTHORING_API}/paths/${seg(key)}/learners/${seg(identifier)}`,
  );

/* -------------------------------------------------------- course learners ---- */

export const listCourseLearners = (courseKey: string) =>
  openedx.get<MemberList>(`${AUTHORING_API}/courses/${seg(courseKey)}/learners`);

export const addCourseLearner = async (
  courseKey: string,
  who: { username?: string; email?: string; email_students?: boolean },
): Promise<RosterWrite> => {
  // `results_after`, NOT `results` — see `RosterWrite`. On this endpoint `results` is the
  // platform's per-address outcome rows, not people.
  const body = await openedx.post<{ email: string; results_after?: unknown }>(
    `${AUTHORING_API}/courses/${seg(courseKey)}/learners`,
    who,
  );
  return { email: body.email, roster: membersOrNull(body.results_after) };
};

export const bulkAddCourseLearners = (
  courseKey: string,
  emails: string[],
  options: { reason?: string; email_students?: boolean } = {},
) =>
  openedx.post<{ emails: string[] }>(`${AUTHORING_API}/courses/${seg(courseKey)}/learners/bulk`, {
    emails,
    ...options,
  });

export const removeCourseLearner = (courseKey: string, identifier: string) =>
  openedx.delete<{ email: string }>(
    `${AUTHORING_API}/courses/${seg(courseKey)}/learners/${seg(identifier)}`,
  );

/* =============================================================== helpers ===== */

/** Steps in the order the author set. The API returns them sorted, the plugin does not. */
export const orderedSteps = (path: AdminPath): AdminPathStep[] =>
  [...path.steps].sort((a, b) => a.order - b.order);

/**
 * May this caller edit this path?
 *
 * Answered from the server's own `writable_orgs`, never from the persona. A tenant admin
 * on a Host that also serves another academy's org sees that academy's paths in the list —
 * legitimately, their learners can take them — and must not be offered controls that will
 * 404. The server is still the boundary; this only decides what to draw.
 */
export const canWrite = (path: AdminPath, writableOrgs: string[]): boolean =>
  writableOrgs.includes(path.org);

/**
 * A path key from a title, in the format the server insists on.
 *
 * `path-v1:{org}+{number}+{run}+{group}`, and the separator is '+', so the number segment
 * must not contain one — nor a ':', nor a space. Everything outside the safe set becomes a
 * hyphen and the result is upper-cased, which is what an Open edX key looks like.
 */
export function derivePathKey(org: string, title: string, run: string, group = 'G1'): string {
  const number =
    title
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'PATH';
  return `path-v1:${org}+${number}+${run}+${group}`;
}

/**
 * What went wrong, in words a person can act on.
 *
 * THE SERVER'S OWN SENTENCE IS THE MESSAGE. Every refusal this API makes arrives as
 * `{"error": "…"}` and already names the thing — "course-v1:… is not available to this
 * academy", "The order must list exactly the path's current steps. Missing: […]". Replacing
 * that with "Something went wrong" would throw away the only part of the response worth
 * reading, so it is shown verbatim and the headline only supplies the category.
 *
 * The headline is deliberately NOT a rewrite of the detail: a 403 from the tenant gate and
 * a 403 from the course gate are the same status and different problems, and the sentence
 * is what tells them apart.
 */
export function pathwayProblem(
  error: unknown,
  /**
   * What the caller was acting on, for the ONE case the server sends no sentence.
   *
   * The same gate now guards pathways, courses and the people on them, and a bare 404 that
   * reads "That learning path is not part of this academy" above a failed course enrolment
   * names the wrong thing. Where the server DID send a sentence — which it does for every
   * refusal this API makes — the sentence names the subject itself and this is unused.
   */
  subject: 'learning path' | 'course' | 'learner' = 'learning path',
): { headline: string; detail: string } {
  if (!(error instanceof ApiError)) {
    return {
      headline: 'The academy did not answer.',
      detail: 'Nothing was changed. This is not something you have done — try again.',
    };
  }

  const sentence = serverSentence(error);

  /*
   * TWO HEADLINES PER STATUS, and which one is used depends on whether the server named the
   * thing itself.
   *
   * A status on this API does not identify the problem. `403` is either the permission class
   * refusing an account with no org role at all, or `assert_course_visible_to_tenant`
   * refusing ONE course — "course-v1:… is not available to this academy." `404` is either a
   * pathway that belongs to another tenant, or `assert_course_exists` finding no such course
   * behind a key. Writing "This academy is not allowed to do that" above a sentence about a
   * single course, or "That learning path is not part of this academy" above one about a
   * course, contradicts the only accurate half of the message.
   *
   * So when the server sent a sentence, the headline stays at the category and the sentence
   * names the subject. The specific wording is kept for the case it was written for: no
   * sentence at all, where a bare status is all anyone would otherwise get.
   */
  const [named, bare] =
    error.status === 400
      ? ['That change was rejected.', 'That change was rejected.']
      : error.status === 403
        ? ['The academy refused that.', `You cannot change ${subject}s in this academy.`]
        : error.status === 404
          ? [
              'The academy could not find that.',
              subject === 'learner'
                ? 'That person is not on this list — they may have been removed already.'
                : `That ${subject} is not part of this academy.`,
            ]
          : error.status === 409
            ? ['That already exists.', 'That already exists.']
            : error.status === 503
              ? [
                  'Learning paths are not installed on this platform.',
                  'Learning paths are not installed on this platform.',
                ]
              : ['The academy refused the change.', 'The academy refused the change.'];

  return {
    headline: sentence ? named : bare,
    detail:
      sentence ??
      (error.status === 404
        ? 'It may have been deleted, or it may belong to another academy.'
        : 'The server gave no reason. Nothing has been changed.'),
  };
}

/** The `error` string out of a DRF-style body, when there is one. */
function serverSentence(error: ApiError): string | null {
  const body = error.body;
  if (body && typeof body === 'object' && 'error' in body) {
    const value = (body as { error: unknown }).error;
    if (typeof value === 'string' && value.trim()) return value;
  }
  return null;
}
